<template>
  <div class="d-flex flex-column">
    <div v-if="!currentQuote">
      <div class="quote-tab-header mb-3">
        <h3 class="page-header-2">Quotes</h3>
        <Button
          v-if="canCreateQuote"
          text="New Quote"
          data-cy="create-quote-btn"
          @click="
            withJobWarnings({
              action: handleCreate,
              record: job,
              context: 'new-quote'
            })
          "
        />
      </div>
      <QuoteGroupsList
        v-if="!currentQuote"
        :quotes="job.quotes"
        @quote-approve-click="handleApproveQuote"
        @quote-force-approve-click="handleForceApproveQuote"
        @decline-quote-click="handleDeclineQuote"
        @edit-quote-click="handleEditQuote"
        @email-quote-click="handleEmailQuote"
        @archive-quote-click="handleArchive"
        @unarchive-quote-click="handleUnarchive"
        @quote-card-click="handleQuoteClick"
        @draft-quote-click="handleDraftQuote"
        @pending-quote-click="handleReadyQuote"
        @clone-quote-click="handleCloneQuote"
        @reactivate-quote-click="handleReactivateQuote"
        @pdf-quote-click="handleDownloadPDF"
        @unapprove-quote-click="handleUnapproveQuote"
      />
      <div v-if="!job.quotes.length" class="no-results-message">
        <i class="fad fa-question-circle"></i>
        No Quotes Available
      </div>
    </div>
    <Transition>
      <div v-if="currentQuote">
        <QuoteBuilder :quote="currentQuote">
          <template #title>
            <h3>
              <Breadcrumbs
                :crumbs="crumbs"
                :root-text="rootName"
                @click="handleCrumbClick"
              />
            </h3>
          </template>
        </QuoteBuilder>
      </div>
    </Transition>
    <DeclineQuoteModal
      v-if="declineModal"
      :quote-data="quote"
      @hide-modal="toggleModal('declineModal')"
    />
    <QuoteApprovalModal
      v-if="approvalModal"
      :quote="quote"
      @hide-modal="toggleModal('approvalModal')"
    />
    <quote-force-approval-modal
      v-if="forceApprovalModal"
      :quote="quote"
      @hide-modal="toggleModal('forceApprovalModal')"
    ></quote-force-approval-modal>
    <EmailQuoteOrInvoiceModal
      v-if="showEmailQuoteModal"
      :quote="quote"
      @hide-modal="showEmailQuoteModal = false"
    />
    <PageLoader v-if="loading" message="Loading..." />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus";
import { getQueryVariables, updateQueryParam } from "@/lib/url.js";

import Button from "@/components/Button.vue";
import PageLoader from "@/components/PageLoader.vue";
import Transition from "@/components/Transition.vue";
import EmailQuoteOrInvoiceModal from "@/EmailQuoteOrInvoice/EmailQuoteOrInvoiceModal.vue";
import DeclineQuoteModal from "@/Job/QuoteBuilder/DeclineQuoteModal.vue";
import QuoteApprovalModal from "@/Job/QuoteBuilder/QuoteApprovalModal.vue";
import QuoteForceApprovalModal from "@/Job/QuoteBuilder/QuoteForceApprovalModal.vue";
import { jobWarnings } from "@/mixins/jobWarnings";
import QuoteBuilder from "../QuoteBuilder/QuoteBuilder.vue";

export default {
  components: {
    EmailQuoteOrInvoiceModal,
    QuoteBuilder,
    Transition,
    Button,
    QuoteApprovalModal,
    QuoteForceApprovalModal,
    DeclineQuoteModal,
    PageLoader
  },
  mixins: [jobWarnings],
  data() {
    return {
      showEmailQuoteModal: false,
      crumbs: [],
      quote: null,
      declineModal: false,
      approvalModal: false,
      forceApprovalModal: false,
      loading: false
    };
  },
  computed: {
    ...mapGetters(["configSupportedForTenant"]),
    ...mapState(["job", "currentQuote"]),
    canCreateQuote() {
      return (
        this.job.links && this.job.links.quote_create && !this.currentQuote
      );
    },
    rootName() {
      return this.crumbs.length ? "Back to Quotes" : "Quotes";
    }
  },
  watch: {
    currentQuote(newValue) {
      if (newValue) {
        this.updateCrumbs(newValue);
      }
    }
  },
  mounted() {
    const { quote_id } = getQueryVariables();
    if (quote_id) {
      this.handleQuoteClick(parseInt(quote_id, 10));
    }
    window.addEventListener("popstate", this.navigateBack);
    EventBus.$on("refresh-quote", this.refreshCurrentQuote);
  },
  beforeUnmount() {
    EventBus.$off("refresh-quote", this.refreshCurrentQuote);
    window.removeEventListener("popstate", this.navigateBack);
    this.setCurrentQuote(null);
  },
  methods: {
    ...mapActions([
      "archiveQuote",
      "unarchiveQuote",
      "createJobQuote",
      "fetchJobPaymentSchedule",
      "refreshCurrentQuote",
      "setCurrentQuote",
      "setQuoteState",
      "cloneQuote",
      "unapproveQuoteSignature"
    ]),
    handleQuoteClick(quoteId) {
      this.selectQuote(this.selectedQuote(quoteId));
    },
    async handleApproveQuote({ id }) {
      if (await this.paymentScheduleBlocksApproval()) return;
      this.quote = this.selectedQuote(id);
      this.toggleModal("approvalModal");
    },
    async handleForceApproveQuote({ id }) {
      if (await this.paymentScheduleBlocksApproval()) return;
      this.quote = this.selectedQuote(id);
      this.toggleModal("forceApprovalModal");
    },
    async paymentScheduleBlocksApproval() {
      if (!this.job?.invoice) return false;
      let plan = null;
      try {
        const response = await this.fetchJobPaymentSchedule(this.job.id);
        plan = response?.schedule ?? null;
      } catch {
        return false;
      }
      if (!plan || plan.split_mode !== "amount") return false;
      const planTotal = (plan.installments || []).reduce(
        (sum, i) => sum + Number(i.value || 0),
        0
      );
      const balanceCents = this.job?.invoice?.financials?.balance_cents ?? 0;
      if (planTotal === balanceCents) return false;
      window.notify?.error(
        "Payment schedule total no longer matches the invoice balance. " +
          "Reconfigure it on the Invoice tab before accepting this quote."
      );
      return true;
    },
    handleDeclineQuote({ id }) {
      this.quote = this.selectedQuote(id);
      this.toggleModal("declineModal");
    },
    handleEditQuote({ id }) {
      this.selectQuote({
        ...this.selectedQuote(id),
        editing: true
      });
    },
    handleEmailQuote({ id }) {
      this.quote = this.selectedQuote(id);
      this.showEmailQuoteModal = true;
    },
    handleArchive({ id, link }) {
      this.loading = true;
      this.setQuoteState(link)
        .then(() => {
          window.notify.success(`Quote ${id} archived successfully!`);
        })
        .finally(() => (this.loading = false));
    },
    handleUnarchive({ id, link }) {
      this.loading = true;
      this.setQuoteState(link)
        .then(() => {
          window.notify.success(`Quote ${id} unarchived successfully!`);
        })
        .finally(() => (this.loading = false));
    },
    handleDraftQuote({ id, link }) {
      this.loading = true;
      this.setQuoteState(link)
        .then(() => {
          window.notify.success(`Quote ${id} has set to draft successfully!`);
        })
        .finally(() => (this.loading = false));
    },
    handleReadyQuote({ id, link }) {
      this.loading = true;
      this.setQuoteState(link)
        .then(() => {
          window.notify.success(
            `Quote ${id} has been set to open successfully!`
          );
        })
        .finally(() => (this.loading = false));
    },
    handleCloneQuote({ id, link }) {
      this.loading = true;
      this.cloneQuote(link)
        .then(() => {
          window.notify.success(`Quote ${id} has been cloned successfully!`);
        })
        .finally(() => (this.loading = false));
    },
    handleReactivateQuote({ id, link }) {
      this.loading = true;
      this.setQuoteState(link)
        .then(() => {
          window.notify.success(
            `Quote ${id} has been reactivated successfully!`
          );
        })
        .finally(() => (this.loading = false));
    },
    handleUnapproveQuote({ id, link }) {
      this.loading = true;
      this.unapproveQuoteSignature(link)
        .then(() => {
          window.notify.success(
            `Quote ${id} is no longer approved. Please obtain a new quote acceptance signature from the customer`
          );
        })
        .finally(() => (this.loading = false));
    },
    toggleModal(modal) {
      this[modal] = !this[modal];
      if (!this[modal]) {
        this.quote = null;
      }
    },
    selectedQuote(id) {
      return this.job.quotes.find((quote) => quote.id === id) || null;
    },
    handleCreate() {
      this.createJobQuote({
        create_url: this.job.links.quote_create,
        data: {
          service_address_id: this.job.address.id
        }
      }).then((quote) => {
        window.notify.success("Quote created successfully!");
        this.selectQuote({
          ...quote,
          editing: true
        });
      });
    },
    selectQuote(quote) {
      this.setCurrentQuote(quote);
      updateQueryParam("quote_id", quote?.id ?? null);
    },
    updateCrumbs(quote) {
      this.crumbs = [
        {
          id: quote.id,
          label: `Quote #${quote.id}`,
          hint: this.displayQuoteStatus(quote.status)
        }
      ];
    },
    handleCrumbClick(crumb) {
      if (crumb.id === "root") {
        this.crumbs = [];
        this.setCurrentQuote(null);
        updateQueryParam("quote_id", null);
      }
    },
    handleDownloadPDF({ link }) {
      window.open(link);
    },
    navigateBack() {
      const params = getQueryVariables();
      if (params.quote_id == undefined) {
        this.setCurrentQuote(null);
      } else {
        const quoteId = parseInt(params.quote_id, 10);
        this.selectQuote(this.selectedQuote(quoteId));
      }
    }
  }
};
</script>

<style lang="scss">
.appointment-row > div {
  margin-right: 2rem;
}
.selected-quote-placeholder {
  text-align: center;
  padding: 30px;
  font-size: 1rem;
  color: var(--gray);
}

.quote-tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

//important ssui overrides
div.quote-card {
  div.quote-info {
    margin-right: 0.75rem !important;
  }
  div.quote-price {
    margin-right: 0.75rem !important;
  }
  img.action-trigger {
    margin-top: 0px;
  }
}
</style>
